import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../utils/constants';

interface CheckoutSessionResponse {
  status: string;
  customer_email?: string;
}

export default function CheckoutReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session_id parameter');
      setLoading(false);
      return;
    }

    const fetchSessionStatus = async () => {
      try {
        setError(null);
        
        // Placeholder endpoint - will be implemented server-side later
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/checkout-session?session_id=${encodeURIComponent(sessionId)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            message: `HTTP ${response.status}: ${response.statusText}`,
          }));
          throw new Error(errorData.message || 'Failed to fetch session status');
        }

        const data: CheckoutSessionResponse = await response.json();
        setSessionStatus(data.status);
        if (data.customer_email) {
          setCustomerEmail(data.customer_email);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch session status';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionStatus();
  }, [sessionId]);

  const getStatusMessage = (status: string | null) => {
    if (!status) return 'Unknown status';
    
    if (status === 'complete') {
      return 'Payment succeeded';
    }
    
    if (status === 'open' || status === 'processing') {
      return 'Payment processing';
    }
    
    return 'Payment failed';
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'text-white/70';
    
    if (status === 'complete') {
      return 'text-green-400';
    }
    
    if (status === 'open' || status === 'processing') {
      return 'text-yellow-400';
    }
    
    return 'text-red-400';
  };

  return (
    <div className="min-h-full flex flex-col items-center justify-center py-12">
      <div className="fluid-container w-full">
        <div className="bg-bg border border-white/20 rounded-xl p-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-6">Payment Status</h1>

          {loading && (
            <div className="text-white/70 mb-6">Loading payment status...</div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {!loading && !error && sessionStatus && (
            <>
              <div className={`text-2xl font-semibold mb-4 ${getStatusColor(sessionStatus)}`}>
                {getStatusMessage(sessionStatus)}
              </div>
              
              {customerEmail && (
                <div className="text-white/70 mb-6">
                  Confirmation sent to: {customerEmail}
                </div>
              )}
            </>
          )}

          {!loading && !sessionId && (
            <div className="text-red-400 mb-6">
              Missing session information
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className="bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
}

